import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Chip,
  Stack,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  CalendarMonth,
  Palette,
  Event,
  Tune,
  DesktopMac,
  Lock,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SITE_ORIGIN, usePageSeo } from './usePageSeo';

const APP_STORE_URL = 'https://apps.apple.com/us/app/quick-menu-calendar/id1594094974';

const screenshots = [
  {
    src: '/quick-menu-calendar/hero.jpg',
    alt: 'Menu Calendar Quick View in the Mac menu bar: a full month, one click away'
  },
  {
    src: '/quick-menu-calendar/quick-add.jpg',
    alt: 'Quick add for a new reminder and a new event beside the calendar popup'
  },
  {
    src: '/quick-menu-calendar/settings-overview.jpg',
    alt: 'Menu Calendar Quick View settings for size, week numbers, the Dock icon, launch at login, and a global shortcut'
  }
];

const features = [
  {
    icon: <CalendarMonth />,
    title: 'A calendar in the menu bar',
    body: 'Open the month from an icon next to the clock. Today is highlighted, and you can step through months without opening the Calendar app.'
  },
  {
    icon: <Event />,
    title: 'Events and reminders',
    body: 'Days with events are marked, and the selected day lists what is on it. Add events and reminders from the popup, and open it with a keyboard shortcut.'
  },
  {
    icon: <Palette />,
    title: 'Themes that match your Mac',
    body: 'Choose System, Sky, Crisp White, Forest, Obsidian, Dusk, Coral, or Amber. Each theme recolors the month and the event list.'
  },
  {
    icon: <Tune />,
    title: 'Sized for the menu bar',
    body: 'Pick a small, medium, or large calendar, then set the date format, day shape, weekday headers, and week numbers. Hide calendars you do not want to see.'
  },
  {
    icon: <DesktopMac />,
    title: 'Stays out of the way',
    body: 'Keep the Dock clear by hiding the app icon, and add it to Login Items so the menu bar calendar is there when you start your Mac.'
  },
  {
    icon: <Lock />,
    title: 'Your calendars stay on your Mac',
    body: 'Events and reminders are read through Apple\'s EventKit on your Mac. Menu Calendar Quick View does not upload them to a server.'
  }
];

const QuickMenuCalendar = () => {
  const navigate = useNavigate();
  usePageSeo({
    title: 'Menu Calendar Quick View | Mac Menu Bar Calendar',
    description: 'Menu Calendar Quick View is a free Mac menu bar calendar by iOS and Mac developer Michael Ellis. See the month, events, and reminders without leaving the menu bar.',
    path: '/MenuCalendarQuickView',
    image: `${SITE_ORIGIN}/quick-menu-calendar/icon.png`,
    keywords: 'Mac menu bar calendar, menu bar app, macOS calendar, events, reminders, Michael Ellis',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Menu Calendar Quick View',
      operatingSystem: 'macOS',
      applicationCategory: 'UtilitiesApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      description: 'A free Mac menu bar calendar for the month, events, and reminders.',
      url: `${SITE_ORIGIN}/MenuCalendarQuickView`,
      image: `${SITE_ORIGIN}/quick-menu-calendar/icon.png`,
      author: {
        '@type': 'Person',
        name: 'Michael R. Ellis',
        jobTitle: 'iOS Software Engineer',
        url: `${SITE_ORIGIN}/`
      }
    }
  });
  const [shotIndex, setShotIndex] = useState(0);
  const shotCount = screenshots.length;
  const showShot = (nextIndex) => {
    setShotIndex((nextIndex + shotCount) % shotCount);
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: '24px',
            textAlign: 'center',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#1a1a1a',
              boxShadow: '0 8px 32px rgba(255,255,255,0.08)'
            }
          }}
        >
          <Box
            component="img"
            src="/quick-menu-calendar/icon.png"
            alt="Menu Calendar Quick View app icon"
            sx={{
              width: { xs: 96, sm: 120 },
              height: { xs: 96, sm: 120 },
              borderRadius: '28px',
              mb: 2
            }}
          />
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.75rem' },
              color: '#2c3e50',
              '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
            }}
          >
            Menu Calendar Quick View
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mt: 1.5,
              fontWeight: 500,
              color: 'text.secondary',
              '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
            }}
          >
            A colorful menu bar calendar for Mac
          </Typography>
          <Typography
            sx={{
              maxWidth: 640,
              mx: 'auto',
              mt: 2,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              lineHeight: 1.7,
              color: 'text.secondary',
              '@media (prefers-color-scheme: dark)': { color: '#c8c8c8' }
            }}
          >
            See the month and what is on your day without opening another window.
            Menu Calendar Quick View lives next to the clock, shows upcoming events and
            reminders, and lets you change the theme, size, and date format from a gear menu.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 3 }}
          >
            {['Free', 'macOS 13 or later', 'Menu bar'].map((label) => (
              <Chip
                key={label}
                label={label}
                sx={{
                  fontWeight: 600,
                  backgroundColor: '#e8ecf4',
                  color: '#4a5366',
                  '@media (prefers-color-scheme: dark)': {
                    backgroundColor: '#333',
                    color: '#eee'
                  }
                }}
              />
            ))}
          </Stack>
          <Box sx={{ mt: 3 }}>
            <Button
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
              sx={{ p: 0, borderRadius: '12px', minWidth: 0, minHeight: 0 }}
            >
              <Box
                component="img"
                src="/appstore.png"
                alt="Download on the App Store"
                sx={{ height: { xs: 44, sm: 52 }, width: 'auto' }}
              />
            </Button>
          </Box>
        </Paper>

        <Stack spacing={3} sx={{ mt: { xs: 2, sm: 4 }, alignItems: 'center', display: { xs: 'flex', md: 'none' } }}>
          {screenshots.map((shot) => (
            <Box
              key={shot.src}
              component="img"
              src={shot.src}
              alt={shot.alt}
              width={1024}
              height={640}
              decoding="async"
              sx={{
                width: '100%',
                maxWidth: 512,
                height: 'auto',
                aspectRatio: '1024 / 640',
                display: 'block',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
              }}
            />
          ))}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ mt: 4, display: { xs: 'none', md: 'flex' } }}
        >
          <IconButton
            aria-label="Previous image"
            onClick={() => showShot(shotIndex - 1)}
            sx={{
              color: '#4a5366',
              '@media (prefers-color-scheme: dark)': { color: '#e8e8e8' }
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Box sx={{ width: '100%', maxWidth: 512, overflow: 'hidden', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <Box
              sx={{
                display: 'flex',
                width: `${shotCount * 100}%`,
                transform: `translateX(-${(shotIndex * 100) / shotCount}%)`,
                transition: 'transform 0.4s ease'
              }}
            >
              {screenshots.map((shot) => (
                <Box
                  key={shot.src}
                  component="img"
                  src={shot.src}
                  alt={shot.alt}
                  width={1024}
                  height={640}
                  decoding="async"
                  sx={{
                    width: `${100 / shotCount}%`,
                    height: 'auto',
                    aspectRatio: '1024 / 640',
                    display: 'block',
                    flexShrink: 0
                  }}
                />
              ))}
            </Box>
          </Box>
          <IconButton
            aria-label="Next image"
            onClick={() => showShot(shotIndex + 1)}
            sx={{
              color: '#4a5366',
              '@media (prefers-color-scheme: dark)': { color: '#e8e8e8' }
            }}
          >
            <ChevronRight />
          </IconButton>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          sx={{ mt: 2, display: { xs: 'none', md: 'flex' } }}
        >
          {screenshots.map((shot, index) => (
            <Box
              key={shot.src}
              component="button"
              type="button"
              aria-label={`Show image ${index + 1}`}
              aria-current={index === shotIndex ? 'true' : undefined}
              onClick={() => showShot(index)}
              sx={{
                width: 10,
                height: 10,
                minWidth: 10,
                minHeight: 10,
                p: 0,
                border: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                backgroundColor: index === shotIndex ? '#4a5366' : 'rgba(74, 83, 102, 0.35)',
                '@media (prefers-color-scheme: dark)': {
                  backgroundColor: index === shotIndex ? '#ffffff' : 'rgba(255, 255, 255, 0.35)'
                }
              }}
            />
          ))}
        </Stack>

        <Typography
          variant="h4"
          component="h2"
          sx={{
            mt: { xs: 5, sm: 7 },
            mb: 3,
            textAlign: 'center',
            fontWeight: 700,
            fontSize: { xs: '1.6rem', sm: '2rem' },
            color: '#2c3e50',
            '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
          }}
        >
          What it does
        </Typography>
        <Grid container spacing={2.5}>
          {features.map((feature) => (
            <Grid item key={feature.title} xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: '20px',
                  backgroundColor: 'white',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  '@media (prefers-color-scheme: dark)': {
                    backgroundColor: '#1a1a1a',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.06)'
                  }
                }}
              >
                <Box sx={{ color: '#4a5366', mb: 1.5, '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' } }}>
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: '#2c3e50',
                    '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
                  }}
                >
                  {feature.body}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: { xs: 4, sm: 6 },
            p: { xs: 3, sm: 4 },
            borderRadius: '20px',
            textAlign: 'center',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#1a1a1a',
              boxShadow: '0 8px 32px rgba(255,255,255,0.06)'
            }
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: '#2c3e50',
              '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
            }}
          >
            Support
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2, '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' } }}>
            Questions about the app:{' '}
            <Box
              component="a"
              href="mailto:Contact@MichaelRobertEllis.com"
              sx={{
                color: '#4a5366',
                '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' }
              }}
            >
              Contact@MichaelRobertEllis.com
            </Box>
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center" alignItems="center">
            <Button component={RouterLink} to="/menu-calendar-quick-view-privacy-policy">
              Privacy Policy
            </Button>
            <Button component={RouterLink} to="/menu-calendar-quick-view-terms">
              Terms and Conditions
            </Button>
          </Stack>
        </Paper>
      </Container>
    </>
  );
};

export default QuickMenuCalendar;
